import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../index.css';

const WorkorderList = () => {
  const [workorders, setWorkorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedWorkorder, setSelectedWorkorder] = useState(null);
  const [users, setUsers] = useState([]);
  const [completedBy, setCompletedBy] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchWorkorders();
    fetchUsers();
  }, []);

  const fetchWorkorders = async () => {
    try {
      const response = await axios.get('/api/workorders');
      setWorkorders(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching workorders:', err);
      setError('Failed to load workorders');
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleCompleteClick = (workorder, e) => {
    e.stopPropagation(); // Prevent row click navigation
    setSelectedWorkorder(workorder);
    setShowCompleteModal(true);
  };

  const handleComplete = async () => {
    if (!completedBy) {
      setError('Please select who completed the workorder');
      return;
    }

    try {
      await axios.put(`/api/workorders/${selectedWorkorder.id}/complete`, {
        assignedTo: completedBy,
        status: 'Completed',
        completedDate: new Date().toISOString()
      });

      setShowCompleteModal(false);
      setCompletedBy('');
      setSelectedWorkorder(null);
      fetchWorkorders(); // Refresh the list
    } catch (err) {
      console.error('Error completing workorder:', err);
      setError('Failed to complete workorder');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Open':
        return 'bg-warning';
      case 'In Progress':
        return 'bg-info';
      case 'Completed':
        return 'bg-success';
      default:
        return 'bg-secondary';
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Cellar Workorders</h2>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate('/workorders/new')}
        >
          + Workorder
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Date</th>
              <th>Wine Lot</th>
              <th>Status</th>
              <th>Activities</th>
              <th>Assigned To</th>
              <th>Completed Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {workorders.map((workorder) => (
              <tr
                key={workorder.id}
                onClick={() => navigate(`/workorders/${workorder.id}`)}
                style={{ cursor: 'pointer' }}
                className="table-row-hover"
              >
                <td>{new Date(workorder.date).toLocaleDateString()}</td>
                <td>{workorder.wineLot}</td>
                <td>
                  <span className={`badge ${getStatusBadgeClass(workorder.status)}`}>
                    {workorder.status}
                  </span>
                </td>
                <td>{workorder.activityCount} activities</td>
                <td>{workorder.assignedTo || '-'}</td>
                <td>
                  {workorder.completedDate 
                    ? new Date(workorder.completedDate).toLocaleDateString() 
                    : '-'}
                </td>
                <td>
                  {workorder.status !== 'Completed' && (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={(e) => handleCompleteClick(workorder, e)}
                    >
                      Complete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Complete Workorder Modal */}
      {showCompleteModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Complete Workorder</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowCompleteModal(false);
                      setCompletedBy('');
                      setSelectedWorkorder(null);
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>Who completed this workorder?</p>
                  <select
                    className="form-control"
                    value={completedBy}
                    onChange={(e) => setCompletedBy(e.target.value)}
                  >
                    <option value="">Select User</option>
                    {users.map(user => (
                      <option key={user.id} value={user.name}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-sml btn-outline-secondary"
                    onClick={() => {
                      setShowCompleteModal(false);
                      setCompletedBy('');
                      setSelectedWorkorder(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={handleComplete}
                  >
                    Complete Workorder
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>
        {`
          .table-row-hover:hover td {
            background-color: rgba(0,0,0,.075);
          }
          .table-row-hover {
            transition: background-color 0.2s ease;
          }
        `}
      </style>
    </div>
  );
};

export default WorkorderList;